import Component from '../component/component';
import Helpers from '../helpers/helpers';
import Navigation from '../navigation/navigation/navigation';

import './header.scss';

const OPEN_CLASS: string = 'is-open';
const OPEN_SEARCH_CLASS: string = 'is-search-open';

export default class Header extends Component {
    static initSelector: string = '.header';

    inner: HTMLElement;
    headerClose: NodeListOf<HTMLButtonElement>;
    navigationElement: HTMLElement;
    navigation: Navigation;
    toggleButton: HTMLElement;
    scrollPosition: number;
    searchToggle: HTMLElement;
    searchContainer: HTMLElement;
    searchClose: NodeListOf<HTMLButtonElement>;
    rightCol: HTMLElement;

    constructor(target: HTMLElement) {
        super(target);

        this.inner = this.element.querySelector('.header__inner');
        this.navigationElement = this.element.querySelector('.header__navigation');
        this.toggleButton = this.element.querySelector('.header__toggle');
        this.headerClose = this.element.querySelectorAll('.navigation-header__button--close, .navigation-alternative-header__button--close');
        this.searchContainer = this.element.querySelector('.header__search-container');
        if (this.searchContainer) {
            this.searchClose = this.searchContainer.querySelectorAll('.navigation-header__button--close, .navigation-alternative-header__button--close');
        }
        this.searchToggle = this.element.querySelector('.header__search-toggle');

        this.rightCol = this.element.querySelector('.header__right-col');

        this.handleScroll = this.handleScroll.bind(this);
        this.handleResize = this.handleResize.bind(this);

        this.init();
    }

    init(): void {
        this.navigation = Navigation.create<typeof Navigation>(this.navigationElement);
        this.toggleButton.addEventListener('click', this.handleToggleButtonClick.bind(this));
        if (this.searchToggle) {
            this.searchToggle.addEventListener('click', this.handleSearchToggleClick.bind(this));
        }
        if (this.headerClose) {
            this.headerClose.forEach((button: HTMLButtonElement) => button.addEventListener('click', this.close.bind(this)));
        }
        if (this.searchClose) {
            this.searchClose.forEach((button: HTMLButtonElement) => button.addEventListener('click', this.closeSearch.bind(this)));
        }
        this.inner.addEventListener('transitionend', this.handleInnerTransitionEnd.bind(this));
        this.inner.addEventListener('keyup', this.handleInnerKeyup.bind(this));

        window.addEventListener('scroll', this.handleScroll);
        window.addEventListener('resize', this.handleResize);

        window.addEventListener('openNavigationItem', this.onNavigationItemOpen.bind(this));
        window.addEventListener('closeNavigationItem', this.onNavigationItemClose.bind(this));

        this.headerHandler();

        if (this.rightCol) {
            this.handleRightColSize();
        }
    }

    get isOpen(): boolean {
        return this.element.classList.contains(OPEN_CLASS);
    }

    get isSearchOpen(): boolean {
        return this.element.classList.contains(OPEN_SEARCH_CLASS);
    }

    handleRightColSize(): void {
        const query: MediaQueryList = window.matchMedia('only screen and (min-width: 1089px)');
        const colLength: number = this.rightCol.clientWidth;

        if (query.matches) {
            this.rightCol.style.width = `${colLength}px`;
            this.rightCol.classList.add('is-absolute');
        } else {
            this.rightCol.style.width = ``;
            this.rightCol.classList.remove('is-absolute');
        }
    }

    open(): void {
        Helpers.disableScroll();

        this.element.classList.add(OPEN_CLASS);
        this.toggleButton.setAttribute('aria-expanded', 'true');

        window.addEventListener('click', this.handleOutsideClick.bind(this), true);
    }

    close(): void {
        this.element.classList.remove(OPEN_CLASS);
        this.toggleButton.setAttribute('aria-expanded', 'false');
        this.toggleButton.focus();

        window.removeEventListener('click', this.handleOutsideClick.bind(this), true);

        Helpers.enableScroll();
    }

    toggle(): void {
        if (this.isOpen) {
            this.close();
        } else {
            this.open();
        }
    }

    handleOutsideClick(event : MouseEvent): void {
        const innerTarget: HTMLElement = document.querySelector('.header__inner');
        const isClickInside: boolean = innerTarget.contains(event.target as Node);

        if (!isClickInside) {
            this.close();
        }
    }

    handleToggleButtonClick(event: MouseEvent): void {
        event.preventDefault();

        this.toggle();
    }

    handleSearchToggleClick(event: MouseEvent): void {
        event.preventDefault();

        if (this.isSearchOpen) {
            this.closeSearch();
        } else {
            this.openSearch();
        }
    }

    closeSearch(): void {
        this.element.classList.remove(OPEN_SEARCH_CLASS);
    }

    openSearch(): void {
        this.element.classList.add(OPEN_SEARCH_CLASS);
    }

    handleInnerTransitionEnd(event: TransitionEvent): void {
        if (event.target === event.currentTarget && event.propertyName === 'visibility') {
            if (Helpers.visible(this.inner)) {
                this.inner.focus();
            }
        }
    }

    handleInnerKeyup(event: KeyboardEvent): void {
        if (event.key === 'Escape') {
            this.close();
        }
    }

    handleScroll(): void {
        if (!Helpers.isScrollDisabled()) {
            const currentPosition: number = window.scrollY;

            // Check whether scroll position is at top or not.
            // Can be less than zero when scrolling past top in Mac OS Safari.
            if (currentPosition <= 0) {
                this.element.classList.remove(...['has-scrolled', 'is-scrolling-up']);
            } else {
                this.element.classList.add('has-scrolled');
            }

            // Check scroll direction.
            // 7 is a magic number so scroll direction would not toggle when scroll jitters.
            if (currentPosition < (this.scrollPosition - 7)) {
                this.element.classList.add('is-scrolling-up');
                this.element.classList.remove('is-scrolling-down');
            } else if (currentPosition > (this.scrollPosition + 7)) {
                this.element.classList.add('is-scrolling-down');
                this.element.classList.remove('is-scrolling-up');
            }

            // Save scroll position for next comparison.
            this.scrollPosition = currentPosition;
        }
    }

    handleResize(): void {
        const query: MediaQueryList = window.matchMedia('only screen and (min-width: 1000px)');

        // Close burger menu after resize to reset it and enable scroll.
        // This is necessary on tablets, for example,
        // where in portrait mobile menu is used
        // and in landscape desktop menu is used.
        if (query.matches && this.isOpen) {
            this.close();
            this.navigation.closeItems();
        }

        if (this.rightCol) {
            this.handleRightColSize();
        }

        this.headerHandler();
    }

    headerHandler(): void {
        document.documentElement.style.setProperty('--header-height', `${this.element.clientHeight}px`);
    }

    onNavigationItemOpen(): void {
        this.element.classList.add('navigation-item-open');
    }

    onNavigationItemClose(): void {
        this.element.classList.remove('navigation-item-open');
    }

    destroy(): void {
        window.removeEventListener('scroll', this.handleScroll);
        window.removeEventListener('resize', this.handleScroll);
        window.removeEventListener('openNavigationItem', this.onNavigationItemOpen.bind(this));
        window.removeEventListener('closeNavigationItem', this.onNavigationItemClose.bind(this));
    }
}
